import Reveal from "../components/Reveal";
import Seo from "../components/Seo";
import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";

const team = [
  { name: "Aanya Kapoor", role: "Founder & Atelier Director", img: "https://images.unsplash.com/photo-1438761681033-6461ffad8d80?w=600&q=80&auto=format&fit=crop" },
  { name: "Vikram Singh", role: "Head of India Atelier", img: "https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=600&q=80&auto=format&fit=crop" },
  { name: "Sophie Laurent", role: "Head of International", img: "https://images.unsplash.com/photo-1573496359142-b8d87734a5a2?w=600&q=80&auto=format&fit=crop" },
  { name: "James Holloway", role: "Concierge Director", img: "https://images.unsplash.com/photo-1500648767791-00dcc994a43e?w=600&q=80&auto=format&fit=crop" },
];

const partners = ["Aman", "Oberoi", "Taj Hotels", "Four Seasons", "Soneva", "One&Only", "Cheval Blanc", "Six Senses"];

export default function About() {
  return (
    <div>
      <Seo
        title="About Voyage Luxe — A Private Luxury Travel Atelier"
        description="Founded in 2017 by Aanya Kapoor. A small atelier of fifteen, hosting fewer guests by design. Eight years, 2,400 guests, sixty-four countries."
      />
      {/* Hero */}
      <section className="pt-40 md:pt-48 pb-20 container-luxe">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-end">
          <Reveal className="lg:col-span-7">
            <div className="eyebrow mb-6">Our Atelier</div>
            <h1 className="font-serif text-5xl md:text-7xl lg:text-8xl leading-[0.95] tracking-tight text-balance">
              A small atelier,<br /><span className="italic">by deliberate choice</span>.
            </h1>
          </Reveal>
          <Reveal className="lg:col-span-5" delay={200}>
            <p className="text-foreground/70 leading-relaxed">
              Voyage Luxe was founded in 2017 by Aanya Kapoor with a single conviction: that the finest journeys cannot be scaled. We accept fewer guests by design — and design more, for each, in return.
            </p>
          </Reveal>
        </div>
      </section>

      {/* Cinematic image */}
      <section>
        <div className="aspect-[16/8] w-full overflow-hidden">
          <img src="https://images.unsplash.com/photo-1582719508461-905c673771fd?w=2400&q=80&auto=format&fit=crop" alt="Atelier" className="h-full w-full object-cover" />
        </div>
      </section>

      {/* Founder story */}
      <section className="py-24 md:py-36 container-luxe">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-16">
          <Reveal className="lg:col-span-5 lg:sticky lg:top-32 h-fit">
            <div className="eyebrow mb-4">The Beginning</div>
            <h2 className="font-serif text-4xl md:text-5xl leading-[1.05] tracking-tight">The atelier was born of a quiet frustration.</h2>
          </Reveal>
          <div className="lg:col-span-7 space-y-8 text-lg text-foreground/75 leading-[1.85]">
            <Reveal>
              <p>Aanya Kapoor had spent eight years in luxury hospitality across Asia and Europe before returning to India. Travelling with discerning clients she observed, repeatedly, the same gap — between the spectacular experiences she knew were possible, and the templated itineraries the market offered.</p>
            </Reveal>
            <Reveal delay={120}>
              <p>So she began Voyage Luxe with three principles: that every journey would be designed from a blank page; that the atelier would never grow beyond what she could personally oversee; and that the concierge desk would answer, day or night, in any time zone, within five rings.</p>
            </Reveal>
            <Reveal delay={240}>
              <p>Nearly a decade on, we remain a small atelier of fifteen — joined by partner teams in every destination we serve. We have welcomed guests from sixty-four countries, designed honeymoons for over two thousand couples, and arranged the impossible perhaps ten thousand times.</p>
            </Reveal>
            <Reveal delay={360}>
              <p className="font-serif italic text-2xl text-foreground pt-4">"The opposite of luxury is not poverty — it is haste."</p>
              <div className="eyebrow mt-3 text-accent">— Aanya Kapoor, Founder</div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* Stats */}
      <section className="py-20 bg-secondary/40 border-y border-border">
        <div className="container-luxe grid grid-cols-2 md:grid-cols-4 gap-10 text-center">
          {[
            { v: "2,400+", l: "Guests Hosted" },
            { v: "64", l: "Countries Of Origin" },
            { v: "98%", l: "Repeat & Referral" },
            { v: "24/7", l: "Concierge Desk" },
          ].map((s, i) => (
            <Reveal key={s.l} delay={i * 100}>
              <div className="font-serif text-5xl md:text-6xl text-accent">{s.v}</div>
              <div className="eyebrow mt-3">{s.l}</div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* Team */}
      <section className="py-24 md:py-36 container-luxe">
        <Reveal>
          <div className="mb-16">
            <div className="eyebrow mb-4">Our People</div>
            <h2 className="font-serif text-4xl md:text-6xl leading-tight tracking-tight max-w-3xl">The voices on the other end of the line.</h2>
          </div>
        </Reveal>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-6 md:gap-10">
          {team.map((m, i) => (
            <Reveal key={m.name} delay={i * 100}>
              <div className="group">
                <div className="aspect-[3/4] overflow-hidden">
                  <img src={m.img} alt={m.name} className="h-full w-full object-cover transition-transform duration-1000 group-hover:scale-105" />
                </div>
                <div className="mt-4 font-serif text-xl md:text-2xl">{m.name}</div>
                <div className="eyebrow text-foreground/55 mt-1">{m.role}</div>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* Partners */}
      <section className="py-20 bg-secondary/40">
        <div className="container-luxe">
          <Reveal>
            <div className="text-center mb-12">
              <div className="eyebrow mb-4">Hospitality Partners</div>
              <h2 className="font-serif text-3xl md:text-4xl">Preferred status with the world's finest.</h2>
            </div>
          </Reveal>
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-8 gap-6 items-center">
            {partners.map((p, i) => (
              <Reveal key={p} delay={i * 60}>
                <div className="text-center font-serif text-xl md:text-2xl text-foreground/60 hover:text-accent transition-colors py-6 border border-border">
                  {p}
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 md:py-32 container-luxe text-center">
        <Reveal>
          <h2 className="font-serif text-4xl md:text-6xl leading-tight tracking-tight max-w-3xl mx-auto text-balance">
            Shall we begin <span className="italic">your journey</span>?
          </h2>
          <Link to="/contact" className="mt-10 inline-flex items-center gap-3 px-10 py-5 bg-foreground text-background hover:bg-accent transition-colors uppercase text-[11px] tracking-[0.3em]" data-testid="about-cta-contact">
            Speak with the concierge <ArrowUpRight size={14} />
          </Link>
        </Reveal>
      </section>
    </div>
  );
}
